// src/pages/Valuation.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import Main from "./Main";
import { makeCompanyNav } from "./nav";
import { request } from "../services/server";
import useKoncernMembers from "../hooks/useKoncernMembers";
import useKoncernScope from "../hooks/useKoncernScope";
import useWorkspaceLifecycle from "../hooks/useWorkspaceLifecycle";
import KoncernScopePicker from "./components/KoncernScopePicker";
import {
  WorkspaceLockOverlay,
  WorkspaceCloseBanner,
  ActivateWorkspaceButton,
  WorkspaceStatusBadge,
} from "./components/WorkspaceLifecycleUI";

import "./css/valuation.css";

// -----------------------
// Helpers
// -----------------------

function formatCurrency(value, currency = "SEK") {
  if (value == null || Number.isNaN(Number(value))) return "–";
  const n = Number(value);
  if (!isFinite(n)) return "–";
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${Math.round(n).toLocaleString("sv-SE")} ${currency}`;
  }
}

function formatNumber(value, decimals = 2) {
  if (value == null || Number.isNaN(Number(value))) return "–";
  const num = Number(value);
  if (!isFinite(num)) return "–";
  return num.toFixed(decimals).replace(".", ",");
}

function parseDecimal(strOrNum) {
  if (strOrNum == null || strOrNum === "") return null;
  if (typeof strOrNum === "number") return isFinite(strOrNum) ? strOrNum : null;

  const normalized = String(strOrNum).replace(",", ".").trim();
  if (!normalized) return null;

  const num = parseFloat(normalized);
  return Number.isNaN(num) ? null : num;
}

// Map the engine's metric keys ("result_after_financial_items:latest", etc.)
// to broker-readable Swedish labels. The keys are <metric>[:<scope>] where
// scope is "latest" or "avg_3y".
function formatMetricUsed(key) {
  if (!key || typeof key !== "string") return "–";
  const [metric, scope] = key.split(":");
  const metricLabels = {
    revenue: "Omsättning",
    ebit: "Rörelseresultat",
    ebitda: "EBITDA",
    result_after_financial_items: "Resultat efter finansiella poster",
  };
  const scopeLabels = {
    latest: "senaste år",
    avg_3y: "snitt 3 år",
  };
  const m = metricLabels[metric] || metric;
  const s = scopeLabels[scope] || scope;
  return s ? `${m} (${s})` : m;
}

function clamp(n, min, max) {
  const x = Number(n);
  if (!isFinite(x)) return min;
  return Math.min(Math.max(x, min), max);
}

function roundSek(n) {
  if (!isFinite(n)) return null;
  return Math.round(n);
}

function buildPayloadInputs(editInputs) {
  const cleaned = {};
  Object.entries(editInputs || {}).forEach(([key, raw]) => {
    if (raw === "" || raw == null) return;
    if (typeof raw === "string") {
      const normalized = raw.replace(",", ".").trim();
      if (!normalized) return;
      cleaned[key] = normalized;
    } else {
      cleaned[key] = raw;
    }
  });
  return cleaned;
}

// IMPORTANT FIX:
// Your factor values are TOP-LEVEL (data.base_multiple, data.size_factor, ...),
// not inside generated_inputs. manual_inputs can exist but be empty {}.
// This initializer prefers a non-empty manual_inputs snapshot, otherwise falls back to top-level.
function buildInitialEditInputs(data) {
  if (!data) return {};

  const hasManual =
    data.manual_inputs && Object.keys(data.manual_inputs || {}).length > 0;

  const source =
    (data.active_source === "manual" && hasManual && data.manual_inputs) ||
    (hasManual && data.manual_inputs) ||
    {};

  const pick = (key) => source[key] ?? data[key] ?? "";

  return {
    base_multiple: pick("base_multiple") !== "" ? String(pick("base_multiple")) : "",
    size_factor: pick("size_factor") !== "" ? String(pick("size_factor")) : "",
    growth_factor: pick("growth_factor") !== "" ? String(pick("growth_factor")) : "",
    profitability_factor:
      pick("profitability_factor") !== "" ? String(pick("profitability_factor")) : "",
    risk_factor: pick("risk_factor") !== "" ? String(pick("risk_factor")) : "",
  };
}

/**
 * Recompute all bands client-side while the user changes sliders.
 * Matches your current payload:
 * - EV_mid = metric_value * adjustedMultiple
 * - low/high = ±20% around mid (0.8x / 1.2x)
 * - Equity = EV + net_cash
 * - Final = Equity
 */
function computeBandsFromInputs({
  metricValue,
  netCash,
  base,
  size,
  growth,
  profitability,
  risk,
}) {
  if (!isFinite(metricValue)) return null;
  if (!isFinite(base)) return null;

  const preRiskMultiple = base * size * growth * profitability;
  const adjustedMultiple = preRiskMultiple * risk;

  const evMid = roundSek(metricValue * adjustedMultiple);
  const evLow = roundSek(evMid * 0.8);
  const evHigh = roundSek(evMid * 1.2);

  const equityMid = netCash == null ? null : roundSek(evMid + netCash);
  const equityLow = netCash == null ? null : roundSek(evLow + netCash);
  const equityHigh = netCash == null ? null : roundSek(evHigh + netCash);

  return {
    multiples: { preRiskMultiple, adjustedMultiple },
    ev: { low: evLow, mid: evMid, high: evHigh },
    equity: { low: equityLow, mid: equityMid, high: equityHigh },
    final: { low: equityLow, mid: equityMid, high: equityHigh },
  };
}

// -----------------------
// Main
// -----------------------

export default function Valuation() {
  const { company } = useParams();
  const navItems = useMemo(() => makeCompanyNav(company), [company]);

  // Koncern scope — lets the broker drill into each subsidiary or view the
  // whole group from the same URL. activeOrgnr is the orgnr we actually
  // hit for single-company fetches; isAllMode means use the koncern endpoint.
  const { koncernInfo } = useKoncernMembers(company);
  const { scope, setScope, activeOrgnr, isAllMode } = useKoncernScope(company);
  const fetchOrgnr = activeOrgnr || company;

  const [data, setData] = useState(null);
  const [editInputs, setEditInputs] = useState({});
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Koncern state
  const [koncernData, setKoncernData] = useState(null);
  const [activeKoncernTab, setActiveKoncernTab] = useState("consolidated");

  // GET valuation + koncern
  useEffect(() => {
    if (!company) {
      setLoading(false);
      setError("Saknar bolags-ID i URL.");
      return;
    }

    const ctrl = new AbortController();
    setLoading(true);
    setError("");

    (async () => {
      try {
        // In "Hela koncernen" mode we only need the aggregated endpoint.
        if (isAllMode) {
          try {
            const kData = await request(`/valuation/workspace/${company}/koncern/`, {
              signal: ctrl.signal,
            });
            setKoncernData(kData);
          } catch {
            setKoncernData(null);
          }
          setData(null);
          setEditInputs({});
          setDirty(false);
          setLoading(false);
          return;
        }

        // Fetch valuation for the scoped company (parent or subsidiary)
        const payload = await request(`/company/company/${fetchOrgnr}/valuation/`, {
          signal: ctrl.signal,
        });

        if (!payload?.hasValuation) {
          setData(null);
          setEditInputs({});
          setDirty(false);
          setError(payload?.detail || "Ingen värdering finns ännu för bolaget.");
        } else {
          setData(payload);
          setEditInputs(buildInitialEditInputs(payload));
          setDirty(false);
        }

        // Try to fetch koncern valuations (will fail gracefully if not a koncern)
        try {
          const kData = await request(`/valuation/workspace/${company}/koncern/`, {
            signal: ctrl.signal,
          });
          if (kData && (kData.company_count > 1 || kData.consolidated)) {
            setKoncernData(kData);
          }
        } catch {
          // Not a koncern or no access — ignore
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error(e);
        const detail = e?.body?.detail || e?.message || "Kunde inte ladda värdering.";
        setData(null);
        setEditInputs({});
        setDirty(false);
        setError(detail);
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [company, fetchOrgnr, isAllMode]);

  const currency = data?.currency || "SEK";

  const hasManualSnapshot =
    !!data?.manual_inputs && Object.keys(data.manual_inputs || {}).length > 0;

  const activeSource = data?.active_source === "manual" ? "manual" : "generated";

  const handleChangeInput = useCallback((field, value) => {
    setEditInputs((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const post = useCallback(
    (path) =>
      request(`/company/${company}/valuation/${path}`, {
        method: "POST",
        body:
          path === "manual-inputs/"
            ? { inputs: buildPayloadInputs(editInputs) }
            : undefined,
      }),
    [company, editInputs]
  );

  const handleSaveManual = useCallback(async () => {
    if (!company || !data) return;

    const inputs = buildPayloadInputs(editInputs);
    if (Object.keys(inputs).length === 0) return;

    setSaving(true);
    setError("");
    try {
      const payload = await post("manual-inputs/");
      setData(payload);
      setEditInputs(buildInitialEditInputs(payload));
      setDirty(false);
    } catch (e) {
      console.error(e);
      setError(e?.body?.detail || e?.message || "Kunde inte spara manuell värdering.");
    } finally {
      setSaving(false);
    }
  }, [company, data, editInputs, post]);

  const handleRevertGenerated = useCallback(async () => {
    if (!company || !data) return;
    setSaving(true);
    setError("");
    try {
      const payload = await post("revert/");
      setData(payload);
      setEditInputs(buildInitialEditInputs(payload));
      setDirty(false);
    } catch (e) {
      console.error(e);
      setError(e?.body?.detail || e?.message || "Kunde inte återställa.");
    } finally {
      setSaving(false);
    }
  }, [company, data, post]);

  const handleUseManual = useCallback(async () => {
    if (!company || !data) return;
    setSaving(true);
    setError("");
    try {
      const payload = await post("use-manual/");
      setData(payload);
      setEditInputs(buildInitialEditInputs(payload));
      setDirty(false);
    } catch (e) {
      console.error(e);
      setError(
        e?.body?.detail || e?.message || "Kunde inte aktivera manuell snapshot."
      );
    } finally {
      setSaving(false);
    }
  }, [company, data, post]);

  // Finalize / reopen — flips `preliminary` on the backend valuation row so
  // the IM-studio valuation gate (`finalized_exists`) can be satisfied.
  const handleFinalize = useCallback(async () => {
    if (!company || !data || dirty) return;
    setSaving(true);
    setError("");
    try {
      const res = await request(`/company/${company}/valuation/finalize/`, { method: "POST" });
      setData((prev) => ({
        ...(prev || {}),
        preliminary: res?.preliminary ?? false,
        finalized_at: res?.finalized_at ?? null,
      }));
    } catch (e) {
      console.error(e);
      setError(e?.body?.detail || e?.message || "Kunde inte slutföra värderingen.");
    } finally {
      setSaving(false);
    }
  }, [company, data, dirty]);

  const handleReopen = useCallback(async () => {
    if (!company || !data) return;
    setSaving(true);
    setError("");
    try {
      const res = await request(`/company/${company}/valuation/reopen/`, { method: "POST" });
      setData((prev) => ({
        ...(prev || {}),
        preliminary: res?.preliminary ?? true,
        finalized_at: res?.finalized_at ?? null,
      }));
    } catch (e) {
      console.error(e);
      setError(e?.body?.detail || e?.message || "Kunde inte återöppna värderingen.");
    } finally {
      setSaving(false);
    }
  }, [company, data]);

  // -----------------------
  // Derived values (live preview)
  // -----------------------
  const metricValue = data?.metric_value != null ? Number(data.metric_value) : NaN;
  const netCash = data?.final_net_cash != null ? Number(data.final_net_cash) : null;

  const parsedInputs = useMemo(() => {
    return {
      base:
        parseDecimal(editInputs.base_multiple) ??
        (data?.base_multiple != null ? Number(data.base_multiple) : null),
      size:
        parseDecimal(editInputs.size_factor) ??
        (data?.size_factor != null ? Number(data.size_factor) : 1),
      growth:
        parseDecimal(editInputs.growth_factor) ??
        (data?.growth_factor != null ? Number(data.growth_factor) : 1),
      profitability:
        parseDecimal(editInputs.profitability_factor) ??
        (data?.profitability_factor != null ? Number(data.profitability_factor) : 1),
      risk:
        parseDecimal(editInputs.risk_factor) ??
        (data?.risk_factor != null ? Number(data.risk_factor) : 1),
    };
  }, [editInputs, data]);

  const liveBands = useMemo(() => {
    if (!data) return null;
    if (!isFinite(metricValue)) return null;
    if (parsedInputs.base == null) return null;

    return computeBandsFromInputs({
      metricValue,
      netCash,
      base: parsedInputs.base,
      size: parsedInputs.size ?? 1,
      growth: parsedInputs.growth ?? 1,
      profitability: parsedInputs.profitability ?? 1,
      risk: parsedInputs.risk ?? 1,
    });
  }, [data, metricValue, netCash, parsedInputs]);

  const serverBands = useMemo(() => {
    if (!data) return null;
    return {
      ev: { low: data.ev_value_low, mid: data.ev_value_mid, high: data.ev_value_high },
      equity: { low: data.equity_value_low, mid: data.equity_value_mid, high: data.equity_value_high },
      final: { low: data.final_value_low, mid: data.final_value_mid, high: data.final_value_high },
      multiples: {
        preRiskMultiple: data.pre_risk_multiple != null ? Number(data.pre_risk_multiple) : null,
        adjustedMultiple: data.adjusted_multiple != null ? Number(data.adjusted_multiple) : null,
      },
    };
  }, [data]);

  const shown = dirty && liveBands ? liveBands : serverBands;

  // -----------------------
  // UI
  // -----------------------
  const { workspace: wsLifecycle, reload: reloadLifecycle } = useWorkspaceLifecycle(company);

  return (
    <Main navItems={navItems}>
      <WorkspaceCloseBanner workspace={wsLifecycle} />
      <WorkspaceLockOverlay workspace={wsLifecycle} onActivate={reloadLifecycle} allowPending>
      {/* Pending-banner med Aktivera-knappen — eftersom värderingen är
          synlig i pending-läget (allowPending) syns inte den vanliga
          lock-overlayens aktivera-knapp här. Banner gör knappen direkt
          tillgänglig på sidan så brokern kan klicka Aktivera direkt
          från värderingsvyn istället för att navigera bort. */}
      {wsLifecycle?.deal_status === "pending" && wsLifecycle?.can_manage_lifecycle && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            padding: "16px 22px",
            margin: "16px auto",
            maxWidth: 1100,
            background: "#FFFBEB",
            border: "1px solid #FBBF24",
            borderRadius: 12,
            color: "#92400E",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
            <WorkspaceStatusBadge status="pending" />
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 600 }}>Workspace är inte aktiverat</div>
              <div>
                Detta är en förhandsvisning. Aktivera workspace för att låsa upp
                IM Studio, datarum, due diligence, pipeline och köparmatchning.
              </div>
            </div>
          </div>
          <ActivateWorkspaceButton workspace={wsLifecycle} onActivated={reloadLifecycle} />
        </div>
      )}
      <div className="val-page">
        <div className="val-header">
          <div>
            <h1 className="val-title">Värdering</h1>
            <div className="val-meta">
              <span className="val-chip">Bolag: {isAllMode ? "Hela koncernen" : (fetchOrgnr || "–")}</span>
              <span className="val-chip">
                Datum:{" "}
                {data?.as_of_date
                  ? new Date(data.as_of_date).toLocaleDateString("sv-SE")
                  : "–"}
              </span>
              <span
                className={`val-chip ${
                  activeSource === "manual" ? "is-manual" : "is-generated"
                }`}
              >
                Källa: {activeSource}
              </span>
              {dirty && <span className="val-chip is-dirty">Förhandsvisning (osparat)</span>}
              {hasManualSnapshot && <span className="val-chip is-snapshot">Manuell snapshot finns</span>}
              {data && (data.preliminary === false ? (
                <span
                  className="val-chip is-finalized"
                  style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}
                  title={
                    data.finalized_at
                      ? `Slutförd ${new Date(data.finalized_at).toLocaleString("sv-SE")}`
                      : "Slutförd"
                  }
                >
                  ✓ Slutförd
                </span>
              ) : (
                <span
                  className="val-chip is-preliminary"
                  style={{ background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A" }}
                >
                  Preliminär
                </span>
              ))}
            </div>
          </div>

          {/* Samma layout som tidigare — alla knappar i en flex-rad i
              val-header-actions. Slutförd värdering grayar ut edit-
              knapparna istället för att tas bort, vilket både ger broker
              visuell hint om varför de inte funkar och stoppar 500:or
              från revert/use-manual-endpoints på finaliserade rader. */}
          {(() => {
            const finalized = data && data.preliminary === false;
            const lockedTitle = finalized ? "Lås upp värderingen via 'Återöppna' för att redigera" : undefined;
            return (
              <div className="val-header-actions" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <KoncernScopePicker koncernInfo={koncernInfo} scope={scope} setScope={setScope} />
                <button
                  className="val-btn primary"
                  onClick={handleSaveManual}
                  disabled={saving || !dirty || isAllMode || finalized}
                  title={lockedTitle}
                >
                  {saving ? "Sparar…" : "Spara"}
                </button>
                <button
                  className="val-btn"
                  onClick={handleRevertGenerated}
                  disabled={saving || activeSource !== "manual" || isAllMode || finalized}
                  title={lockedTitle}
                >
                  Återgå till genererad
                </button>
                <button
                  className="val-btn"
                  onClick={handleUseManual}
                  disabled={saving || !hasManualSnapshot || activeSource === "manual" || isAllMode || finalized}
                  title={lockedTitle}
                >
                  Använd senaste manuella
                </button>
                {data && (data.preliminary === false ? (
              <button
                className="val-btn"
                onClick={handleReopen}
                disabled={saving || isAllMode}
                title="Lås upp värderingen för redigering"
              >
                Återöppna
              </button>
            ) : (
              <button
                className="val-btn primary"
                onClick={handleFinalize}
                disabled={saving || dirty || isAllMode}
                title={
                  dirty
                    ? "Spara dina ändringar först"
                    : "Markera värderingen som klar — låser upp värderingsavsnittet i IM-studio"
                }
                style={{ background: "#047857", borderColor: "#047857" }}
              >
                Slutför värdering
              </button>
            ))}
              </div>
            );
          })()}
        </div>

        {error && (
          <div className="val-alert">
            <strong>Fel:</strong> {error}
          </div>
        )}

        {loading && <div className="val-loading">Hämtar värdering …</div>}

        {/* KONCERN ACCORDION */}
        {!loading && koncernData && (
          <section className="val-card" style={{ marginBottom: 20 }}>
            <div className="val-cardhead">
              <h2 className="val-h2">Koncernvärdering</h2>
              <div className="val-muted">{koncernData.company_count} bolag</div>
            </div>

            {/* Tab buttons */}
            <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e5e7eb", marginBottom: 16 }}>
              {koncernData.consolidated && (
                <button
                  type="button"
                  onClick={() => setActiveKoncernTab("consolidated")}
                  style={{
                    padding: "10px 20px", fontSize: 13, fontWeight: 600, border: "none",
                    background: "none", cursor: "pointer",
                    borderBottom: activeKoncernTab === "consolidated" ? "2px solid #57534E" : "2px solid transparent",
                    color: activeKoncernTab === "consolidated" ? "#57534E" : "#6b7280",
                    marginBottom: -2,
                  }}
                >
                  Konsoliderad
                  {koncernData.consolidated?.value_mid != null && (
                    <span style={{ marginLeft: 8, fontWeight: 400, color: "#9ca3af" }}>
                      {formatCurrency(koncernData.consolidated.value_mid)}
                    </span>
                  )}
                </button>
              )}
              {koncernData.individual.map((comp) => (
                <button
                  key={comp.orgnr}
                  type="button"
                  onClick={() => setActiveKoncernTab(comp.orgnr)}
                  style={{
                    padding: "10px 20px", fontSize: 13, fontWeight: 600, border: "none",
                    background: "none", cursor: "pointer",
                    borderBottom: activeKoncernTab === comp.orgnr ? "2px solid #57534E" : "2px solid transparent",
                    color: activeKoncernTab === comp.orgnr ? "#57534E" : "#6b7280",
                    marginBottom: -2,
                  }}
                >
                  {comp.name}
                  {comp.valuation?.value_mid != null && (
                    <span style={{ marginLeft: 8, fontWeight: 400, color: "#9ca3af" }}>
                      {formatCurrency(comp.valuation.value_mid)}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Active tab content */}
            {activeKoncernTab === "consolidated" && koncernData.consolidated && (
              <KoncernValuationCard val={koncernData.consolidated} label="Konsoliderad koncernvärdering" />
            )}
            {koncernData.individual.map((comp) =>
              activeKoncernTab === comp.orgnr && comp.valuation ? (
                <KoncernValuationCard key={comp.orgnr} val={comp.valuation} label={comp.name} />
              ) : null
            )}
            {koncernData.individual.map((comp) =>
              activeKoncernTab === comp.orgnr && !comp.valuation ? (
                <div key={comp.orgnr} className="val-muted" style={{ padding: 20, textAlign: "center" }}>
                  Ingen värdering tillgänglig för {comp.name}. Fyll i riskprofil för att generera.
                </div>
              ) : null
            )}
          </section>
        )}

        {!loading && data && (
          <>
            {/* HERO: main valuation */}
            <section className="val-hero">
              <div className="val-hero-left">
                <div className="val-hero-kicker">Värderingsintervall</div>

                <div className="val-hero-range">
                  {formatCurrency(shown?.final?.low, currency)} – {formatCurrency(shown?.final?.high, currency)}
                </div>

                <div className="val-hero-mid">
                  {formatCurrency(shown?.final?.mid, currency)}
                </div>

                <div className="val-hero-sub">
                  Nyckeltal: <b>{formatMetricUsed(data.metric_used)}</b>{" "}
                  <span className="val-dot">•</span>{" "}
                  Värde: <b>{formatCurrency(data.metric_value, currency)}</b>
                </div>
              </div>

              <div className="val-hero-right">
                <MiniStat label="Företagsvärde (mitt)" value={formatCurrency(shown?.ev?.mid, currency)} />
                <MiniStat label="Nettokassa" value={formatCurrency(data.final_net_cash, currency)} />
                <MiniStat label="Multipel (justerad)" value={`${formatNumber(shown?.multiples?.adjustedMultiple, 3)}x`} />
              </div>
            </section>

            {/* DATAKVALITET — visas före sliders så mäklaren ser vad som
                påverkar intervallet innan de börjar justera. */}
            <CoverageCard data={data} company={company} />

            {/* SLIDERS */}
            <section className="val-card">
              <div className="val-cardhead">
                <div>
                  <h2 className="val-h2">Justera värdering</h2>
                  <div className="val-muted">
                    Reglagen uppdaterar Företagsvärde/Eget kapital/Slutligt direkt. Klicka <b>Spara</b> för att skicka till servern.
                  </div>
                </div>

                <div className="val-multiples">
                  <div className="val-pillbox">
                    före risk: <b>{formatNumber(shown?.multiples?.preRiskMultiple, 3)}x</b>
                  </div>
                  <div className="val-pillbox">
                    justerad: <b>{formatNumber(shown?.multiples?.adjustedMultiple, 3)}x</b>
                  </div>
                </div>
              </div>

              <div className="val-sliders">
                {/* IMPORTANT CHANGE: min=0.1 (no zeros) */}
                <SliderField
                  label="Grundmultipel"
                  name="base_multiple"
                  value={editInputs.base_multiple}
                  onChange={handleChangeInput}
                  min={0.1}
                  max={20}
                  step={0.01}
                  unit="x"
                />
                <SliderField
                  label="Storleksfaktor"
                  name="size_factor"
                  value={editInputs.size_factor}
                  onChange={handleChangeInput}
                  min={0.1}
                  max={2}
                  step={0.01}
                />
                <SliderField
                  label="Tillväxtfaktor"
                  name="growth_factor"
                  value={editInputs.growth_factor}
                  onChange={handleChangeInput}
                  min={0.1}
                  max={2}
                  step={0.01}
                />
                <SliderField
                  label="Lönsamhetsfaktor"
                  name="profitability_factor"
                  value={editInputs.profitability_factor}
                  onChange={handleChangeInput}
                  min={0.1}
                  max={2}
                  step={0.01}
                />
                <SliderField
                  label="Riskfaktor"
                  name="risk_factor"
                  value={editInputs.risk_factor}
                  onChange={handleChangeInput}
                  min={0.1}
                  max={2}
                  step={0.01}
                />
              </div>
            </section>

            {/* SECTIONS */}
            <div className="val-sections">
              {/* DEMO: Intervall-sektionen är dold. Flippa false→true för att återställa. */}
              {false && (
                <section className="val-card">
                  <div className="val-cardhead">
                    <h2 className="val-h2">Intervall</h2>
                    <div className="val-muted">Låg / Mitt / Hög</div>
                  </div>

                  <BandRow label="Företagsvärde" currency={currency} a={shown?.ev?.low} b={shown?.ev?.mid} c={shown?.ev?.high} />
                  <BandRow label="Eget kapital" currency={currency} a={shown?.equity?.low} b={shown?.equity?.mid} c={shown?.equity?.high} />
                  <BandRow label="Slutligt" currency={currency} a={shown?.final?.low} b={shown?.final?.mid} c={shown?.final?.high} />
                </section>
              )}

              <section className="val-card">
                <div className="val-cardhead">
                  <h2 className="val-h2">Faktorer</h2>
                  <div className="val-muted">Serverdata</div>
                </div>

                <div className="val-row">
                  <div className="val-row-label">Bransch</div>
                  <div className="val-row-value">
                    {data.branch_label || "–"}{" "}
                    <span className="val-muted">({data.branch_key || "–"})</span>
                  </div>
                </div>

                <div className="val-row">
                  <div className="val-row-label">System grundmultipel</div>
                  <div className="val-row-value">{formatNumber(data.base_multiple, 2)}x</div>
                </div>

                <div className="val-row">
                  <div className="val-row-label">System multipel före risk</div>
                  <div className="val-row-value">{formatNumber(data.pre_risk_multiple, 3)}x</div>
                </div>

                <div className="val-row">
                  <div className="val-row-label">System justerad multipel</div>
                  <div className="val-row-value">{formatNumber(data.adjusted_multiple, 3)}x</div>
                </div>

                <div className="val-divider" />

                <div className="val-facts">
                  <div>
                    <div className="val-facts-label">Nyckeltal</div>
                    <div className="val-facts-value">{formatMetricUsed(data.metric_used)}</div>
                  </div>
                  <div>
                    <div className="val-facts-label">Nyckeltalsvärde</div>
                    <div className="val-facts-value">{formatCurrency(data.metric_value, currency)}</div>
                  </div>
                  <div>
                    <div className="val-facts-label">Nettokassa</div>
                    <div className="val-facts-value">{formatCurrency(data.final_net_cash, currency)}</div>
                  </div>
                </div>
              </section>

              {/* DEMO: ML-förslag-sektionen är dold. Flippa false→true för att återställa. */}
              {false && (
                <section className="val-card">
                  <div className="val-cardhead">
                    <h2 className="val-h2">ML-förslag</h2>
                    <div className="val-muted">Om det finns sparat</div>
                  </div>

                  {data?.inputs?.ml_suggestion ? (
                    <>
                      <div className="val-row">
                        <div className="val-row-label">engine_multiple</div>
                        <div className="val-row-value">{data.inputs.ml_suggestion.engine_multiple}</div>
                      </div>
                      <div className="val-row">
                        <div className="val-row-label">delta_multiple</div>
                        <div className="val-row-value">{data.inputs.ml_suggestion.delta_multiple}</div>
                      </div>
                      <div className="val-row">
                        <div className="val-row-label">suggested_multiple</div>
                        <div className="val-row-value">{data.inputs.ml_suggestion.suggested_multiple}</div>
                      </div>
                    </>
                  ) : (
                    <div className="val-muted">Inget ML-förslag sparat.</div>
                  )}
                </section>
              )}
            </div>
          </>
        )}
      </div>
      </WorkspaceLockOverlay>
    </Main>
  );
}

// -----------------------
// Components
// -----------------------

function MiniStat({ label, value }) {
  return (
    <div className="val-ministat">
      <div className="val-ministat-label">{label}</div>
      <div className="val-ministat-value">{value}</div>
    </div>
  );
}

// Coverage card — shows which data sources are filled in and which are still
// missing. Each missing source widens the valuation band by ~6-12%, so filling
// them narrows the spread. Deep-link to the form for each missing item.
function CoverageCard({ data, company }) {
  const cov = (data && data.inputs && data.inputs.coverage) || {};
  const rows = [
    {
      label: "Riskprofil",
      ok: !!cov.has_risk_profile,
      to: `/duediligence/${company}/`,
    },
    {
      label: "Kundkoncentration",
      ok: !!cov.has_customer_concentration,
      to: `/duediligence/${company}/`,
    },
    {
      label: "SIE-fil (senaste 12 mån)",
      ok: !!cov.has_sie_ltm,
      to: `/check?orgnr=${company}`,
    },
    {
      label: "DD-underlag",
      ok: !!cov.has_ddq,
      to: `/duediligence/${company}/`,
    },
    {
      label: "Ägarfrågor",
      ok: !!cov.has_owner_questionnaire,
      to: `/duediligence/${company}/`,
    },
  ];

  const done = rows.filter((r) => r.ok).length;
  const total = rows.length;

  return (
    <section className="val-card">
      <div className="val-cardhead">
        <h2 className="val-h2">Datakvalitet</h2>
        <div className="val-muted">{done} / {total} källor fyllda</div>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {rows.map((r) => (
          <li
            key={r.label}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "8px 0",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <span
              style={{
                fontSize: 16,
                lineHeight: 1.2,
                color: r.ok ? "#16a34a" : "#9ca3af",
                fontWeight: 700,
                width: 18,
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {r.ok ? "✓" : "○"}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: r.ok ? "#374151" : "#111827" }}>
                {r.label}
              </div>
            </div>
            {!r.ok && (
              <a
                href={r.to}
                style={{
                  fontSize: 12,
                  color: "#2563eb",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                }}
              >
                Fyll i →
              </a>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function BandRow({ label, currency, a, b, c }) {
  return (
    <div className="val-band">
      <div className="val-band-label">{label}</div>
      <div className="val-band-values">
        <span>{formatCurrency(a, currency)}</span>
        <span>{formatCurrency(b, currency)}</span>
        <span>{formatCurrency(c, currency)}</span>
      </div>
    </div>
  );
}

function SliderField({
  label,
  name,
  value,
  onChange,
  min,
  max,
  step,
  neutralHint,
  unit,
}) {
  const num = parseDecimal(value);
  const safe = clamp(num ?? min, min, max);

  return (
    <div className="val-slider">
      <div className="val-slider-head">
        <div className="val-slider-left">
          <div className="val-slider-label">
            {label}{" "}
            {neutralHint ? <span className="val-neutral">neutral {neutralHint}</span> : null}
          </div>
          <div className="val-slider-scale">
            <span>{String(min).replace(".", ",")}</span>
            <span>{String(max).replace(".", ",")}</span>
          </div>
        </div>

        <div className="val-slider-inputwrap">
          <input
            className="val-input"
            type="number"
            step={step}
            min={min}
            max={max}
            value={num ?? ""}
            onChange={(e) => onChange(name, e.target.value)}
          />
          {unit ? <span className="val-input-unit">{unit}</span> : null}
        </div>
      </div>

      <input
        className="val-range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={safe}
        onChange={(e) => onChange(name, e.target.value)}
      />
    </div>
  );
}

function KoncernValuationCard({ val, label }) {
  if (!val) return null;
  const currency = "SEK";

  return (
    <div style={{ padding: "0 4px" }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", marginBottom: 8 }}>{label}</div>

      {/* Value range */}
      <div style={{
        display: "flex", alignItems: "center", gap: 16,
        padding: "16px 20px", background: "#f9fafb", borderRadius: 10, marginBottom: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Intervall</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {formatCurrency(val.value_low, currency)} – {formatCurrency(val.value_high, currency)}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Mitt</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#57534E" }}>
            {formatCurrency(val.value_mid, currency)}
          </div>
        </div>
      </div>

      {/* Key metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
        <div style={{ padding: "10px 12px", background: "#fff", border: "1px solid #f3f4f6", borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase" }}>Nyckeltal</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{val.metric_used || "–"}</div>
        </div>
        <div style={{ padding: "10px 12px", background: "#fff", border: "1px solid #f3f4f6", borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase" }}>Multipel</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{val.adjusted_multiple ? `${Number(val.adjusted_multiple).toFixed(2)}x` : "–"}</div>
        </div>
        <div style={{ padding: "10px 12px", background: "#fff", border: "1px solid #f3f4f6", borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase" }}>Bransch</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{val.branch_label || "–"}</div>
        </div>
        <div style={{ padding: "10px 12px", background: "#fff", border: "1px solid #f3f4f6", borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase" }}>Riskprofil</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{val.risk_profile_used ? "Ja" : "Nej"}</div>
        </div>
      </div>

      {/* Equity values if available */}
      {val.equity_value_mid != null && (
        <div style={{ marginTop: 8, padding: "10px 12px", background: "#F5F5F4", borderRadius: 8, fontSize: 13 }}>
          <span style={{ color: "#6b7280" }}>Eget kapital: </span>
          <span style={{ fontWeight: 600 }}>
            {formatCurrency(val.equity_value_low, currency)} – {formatCurrency(val.equity_value_high, currency)}
          </span>
          <span style={{ color: "#57534E", fontWeight: 700, marginLeft: 12 }}>
            {formatCurrency(val.equity_value_mid, currency)}
          </span>
        </div>
      )}
    </div>
  );
}
